import eventBus, { CUSTOM_EVENTS } from '../../react/utils/EventBus';

// CSS class constants
const PANE_CLASSES = {
  ACTIVE: 'mobile-navigation__pane--active',
  PREVIOUS: 'mobile-navigation__pane--previous',
  EXIT_RIGHT: 'mobile-navigation__pane--exit-to-right',
} as const;

const NAV_CLASSES = {
  OPEN: 'mobile-navigation--open',
  SUB: 'mobile-navigation--sub',
} as const;

export function initMobileNavigation(): void {
  const menuToggle = document.querySelector<HTMLButtonElement>('[data-component="menu-toggle"]');
  const mobileNav = document.querySelector<HTMLDialogElement>('[data-component="mobile-navigation"]');

  if (!menuToggle || !mobileNav) return;

  const closeButtons = mobileNav.querySelectorAll<HTMLButtonElement>('[data-js="menu-close"]');
  const paneLinks = mobileNav.querySelectorAll<HTMLAnchorElement>('[data-target-pane]:not([data-back-button])');
  const headerBack = mobileNav.querySelector<HTMLButtonElement>('[data-back-button]');
  const allPanes = mobileNav.querySelectorAll<HTMLElement>('[data-pane-id]');
  const mainPane = mobileNav.querySelector<HTMLElement>('[data-pane-id="main"]');

  const openMenu = () => {
    mobileNav.showModal();
    mobileNav.classList.add(NAV_CLASSES.OPEN);
    document.body.style.overflow = 'hidden';
    menuToggle.setAttribute('aria-expanded', 'true');
  };

  const closeMenu = () => {
    mobileNav.close();
  };

  /**
   * Determines parent pane ID based on hierarchical naming convention:
   * - "pane-services-2" (3rd level) → "pane-services" (2nd level)
   * - "pane-services" (2nd level) → "main" (root)
   * - "main" → null (no parent)
   */
  const getParentPaneId = (currentPaneId: string): string | null => {
    if (currentPaneId === 'main') return null;

    // Third level: pane-{name}-{index}
    const thirdLevelMatch = /^pane-(.+)-\d+$/.exec(currentPaneId);
    if (thirdLevelMatch) {
      return `pane-${thirdLevelMatch[1]}`;
    }

    // Second level: pane-{name}
    if (/^pane-[^-]+$/.test(currentPaneId)) {
      return 'main';
    }

    return null;
  };

  const updateBackButton = (currentPaneId: string) => {
    if (!headerBack) return;

    const parentPaneId = getParentPaneId(currentPaneId);
    if (!parentPaneId) return;

    headerBack.dataset.targetPane = parentPaneId;

    const parentPane = mobileNav.querySelector<HTMLElement>(`[data-pane-id="${parentPaneId}"]`);
    if (!parentPane) return;

    const titleElement = parentPane.querySelector('.mobile-navigation__pane-title');
    const titleText = titleElement?.textContent?.trim().toLowerCase() || 'main menu';
    const backTextSpan = headerBack.querySelector('span:last-child');

    if (backTextSpan) {
      backTextSpan.textContent = `Back to ${titleText}`;
    }
  };

  const removeClassList = (element: Element, classes: readonly string[]) => {
    element.classList.remove(...classes);
  };

  const switchPane = (paneId: string, isBackNavigation = false) => {
    const activePane = mobileNav.querySelector<HTMLElement>(`.${PANE_CLASSES.ACTIVE}`);
    const targetPane = mobileNav.querySelector<HTMLElement>(`[data-pane-id="${paneId}"]`);
    if (!targetPane || targetPane === activePane) return;

    if (activePane) {
      if (isBackNavigation) {
        // Back navigation: current pane exits right, target comes in from left
        removeClassList(activePane, [PANE_CLASSES.ACTIVE]);
        activePane.classList.add(PANE_CLASSES.EXIT_RIGHT);

        removeClassList(targetPane, [PANE_CLASSES.PREVIOUS, PANE_CLASSES.EXIT_RIGHT]);
        targetPane.classList.add(PANE_CLASSES.ACTIVE);

        const cleanup = (ev: TransitionEvent) => {
          if (ev.propertyName === 'transform') {
            activePane.classList.remove(PANE_CLASSES.EXIT_RIGHT);
            activePane.removeEventListener('transitionend', cleanup);
          }
        };

        activePane.addEventListener('transitionend', cleanup);
      } else {
        // Forward navigation: current pane slides left, target enters from right
        removeClassList(activePane, [PANE_CLASSES.ACTIVE]);
        activePane.classList.add(PANE_CLASSES.PREVIOUS);

        removeClassList(targetPane, [PANE_CLASSES.PREVIOUS, PANE_CLASSES.EXIT_RIGHT]);
        targetPane.classList.add(PANE_CLASSES.ACTIVE);
      }
    } else {
      // First navigation (no active pane yet)
      targetPane.classList.add(PANE_CLASSES.ACTIVE);
    }

    // Toggle sub modifier based on target pane
    mobileNav.classList.toggle(NAV_CLASSES.SUB, paneId !== 'main');

    // Update inert state to prevent focus on hidden panes
    allPanes.forEach(pane => {
      pane.inert = pane !== targetPane;
    });

    updateBackButton(paneId);
  };

  menuToggle.addEventListener('click', openMenu);
  closeButtons.forEach(button => button.addEventListener('click', closeMenu));

  // Handle all dialog closes (ESC, backdrop click, or programmatic close)
  mobileNav.addEventListener('close', () => {
    mobileNav.classList.remove(NAV_CLASSES.OPEN, NAV_CLASSES.SUB);
    document.body.style.overflow = '';
    menuToggle.setAttribute('aria-expanded', 'false');

    // Reset all panes to default state
    allPanes.forEach(pane => {
      removeClassList(pane, Object.values(PANE_CLASSES));
      pane.inert = true; // All panes start inert when nav closes
    });

    // Set main to active
    if (mainPane) {
      mainPane.classList.add(PANE_CLASSES.ACTIVE);
      mainPane.inert = false;
    }

    updateBackButton('main');
    menuToggle.focus();
  });

  paneLinks.forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      const paneId = link.dataset.targetPane;
      if (paneId) {
        switchPane(paneId);
      }
    });
  });

  // Handle tabbed menu item links (data-menu-item-index)
  const menuItemLinks = mobileNav.querySelectorAll<HTMLAnchorElement>('[data-menu-item-index]');
  menuItemLinks.forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();

      const parentContainer = link.closest('[data-parent-pane]') as HTMLElement | null;
      if (!parentContainer) return;

      const parentPaneId = parentContainer.dataset.parentPane;
      const menuItemIndex = link.dataset.menuItemIndex;

      if (parentPaneId && menuItemIndex !== undefined) {
        const thirdLevelPaneId = `pane-${parentPaneId}-${menuItemIndex}`;
        switchPane(thirdLevelPaneId);
      }
    });
  });

  // Header back button
  if (headerBack) {
    headerBack.addEventListener('click', () => {
      const currentPane = mobileNav.querySelector<HTMLElement>(`.${PANE_CLASSES.ACTIVE}`);
      if (!currentPane) return;

      const currentPaneId = currentPane.dataset.paneId || 'main';
      const parentPaneId = getParentPaneId(currentPaneId);

      if (parentPaneId) {
        switchPane(parentPaneId, true);
      }
    });
  }

  // Listen for event to close mobile navigation
  eventBus.on(CUSTOM_EVENTS.CLOSE_MOBILE_NAVIGATION, () => {
    if (mobileNav.open) {
      closeMenu();
    }
  });

  updateBackButton('main');
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileNavigation);
} else {
  initMobileNavigation();
}
